import { Component, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { CrossTabSyncService } from './core/services/cross-tab-sync.service';
import { SessionExpiryWatcherService } from './core/services/session-expiry-watcher.service';
import { isSupportedLanguage, supportedLanguage } from './core/i18n/supported-languages';
import { LanguageSwitchService } from './core/i18n/language-switch.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
  host: {
    '[class.app-standard-tables]': 'standardTableStylesEnabled',
  },
})
export class App {
  private static readonly THEME_STORAGE_KEY = 'speed.theme';
  private static readonly LIGHT_THEME = 'speed-light';
  private static readonly DARK_THEME = 'speed-dark';

  protected title = 'speed-frontend';
  private readonly sessionExpiryWatcher = inject(SessionExpiryWatcherService);
  private readonly crossTabSync = inject(CrossTabSyncService);
  private readonly languageSwitch = inject(LanguageSwitchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  protected standardTableStylesEnabled = !this.isAccountingReportRoute(this.router.url);

  constructor() {
    const savedTheme = localStorage.getItem(App.THEME_STORAGE_KEY);
    const resolvedTheme = (savedTheme === App.DARK_THEME || savedTheme === App.LIGHT_THEME)
      ? savedTheme
      : (window.matchMedia('(prefers-color-scheme: dark)').matches ? App.DARK_THEME : App.LIGHT_THEME);

    this.applyTheme(resolvedTheme);

    this.crossTabSync.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.eventType === 'ORG_CHANGED') {
          if (event.payload.sessionId === localStorage.getItem('speed.auth_session_id')) {
            this.reloadPage();
          }
          return;
        }
        if (event.eventType === 'AUTH_LOGIN') {
          if (event.payload.sessionId === localStorage.getItem('speed.auth_session_id')) {
            this.reloadPage();
          }
          return;
        }

        if (event.eventType === 'THEME_CHANGED') {
          this.applyTheme(event.payload.theme);
          return;
        }

        if (event.eventType === 'LANG_CHANGED') {
          const language = event.payload.language?.trim();
          if (!isSupportedLanguage(language)) {
            return;
          }

          this.languageSwitch.switchTo(language, {
            activated: () => {
              localStorage.setItem('speed.lang', language);
              document.documentElement.lang = supportedLanguage(language).htmlLang;
            },
          });
        }
      });

    this.sessionExpiryWatcher.start();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.standardTableStylesEnabled = !this.isAccountingReportRoute(event.urlAfterRedirects);
      });
  }

  private isAccountingReportRoute(url: string): boolean {
    return /^\/accounting\/reports(?:[/?#]|$)/.test(url);
  }

  private reloadPage(): void {
    window.location.reload();
  }

  private applyTheme(theme: string): void {
    const resolved = theme === App.DARK_THEME ? App.DARK_THEME : App.LIGHT_THEME;
    document.documentElement.setAttribute('data-theme', resolved);
    localStorage.setItem(App.THEME_STORAGE_KEY, resolved);
  }
}
